import { render } from 'preact';
import App from './app';
import { html } from '@shared/utils/rendering/html';
import { NavigationContext } from './core/utils/context/navigation-context';

import { AuthProvider } from '@core/utils/context/authentication-context';
import { registerUnauthorizedHandler } from '@shared/hooks/http';
import { apiBase } from './core/utils/api/api-base';

// Venus's ApexCharts wrapper (useChart) destroys and recreates every chart when the host toggles
// `data-venus-theme` on <html> after our remote has mounted. ApexCharts' in-flight async render()
// then lands on a now-detached element and rejects with "Element not found" (and follow-on
// "reading 'filter'/'querySelectorAll'" TypeErrors). The chart re-renders correctly once the theme
// settles, so these rejections are benign — they only appear when consumed in the MF host, never
// standalone. Swallow only those: match "Element not found" by message, and match follow-on
// TypeErrors by message + an ApexCharts (or micro-loader) stack frame, so they don't spam logs.
//
// The listener is scoped to RDARR's mounted lifetime — installed on mount(), removed on unmount() —
// so it never suppresses ApexCharts rejections from the host or other remotes once RDARR is gone.
// A ref-count keeps a single listener across concurrent/overlapping mounts.
const swallowBenignApexNoise = (event) => {
    const reason = event?.reason;
    const message = typeof reason?.message === 'string' ? reason.message : '';
    const stack = typeof reason?.stack === 'string' ? reason.stack.toLowerCase() : '';

    // ApexCharts throws this exact message from its own render path when the container element
    // has already been detached (theme toggle or tab switch remounting the chart mid-render).
    // The message is ApexCharts-specific, so match it regardless of what frames survive on the stack.
    const isDetachedElementError = message === 'Element not found';

    // Follow-on TypeErrors as ApexCharts reads its now-undefined internal state during that torn-down
    // render. These messages are generic, so only swallow them when the stack still points at the
    // chart render path — either ApexCharts itself or the MF micro-loader continuation that drives it.
    const isTornDownStateError =
        (message.includes("reading 'querySelectorAll'") || message.includes("reading 'filter'")) &&
        (stack.includes('apexcharts') || stack.includes('micro-loader'));

    if (isDetachedElementError || isTornDownStateError) {
        event.preventDefault();
    }
};

let apexNoiseGuardRefCount = 0;

const installApexNoiseGuard = () => {
    if (typeof window === 'undefined') return;
    if (apexNoiseGuardRefCount === 0) {
        window.addEventListener('unhandledrejection', swallowBenignApexNoise);
    }
    apexNoiseGuardRefCount += 1;
};

const removeApexNoiseGuard = () => {
    if (typeof window === 'undefined' || apexNoiseGuardRefCount === 0) return;
    apexNoiseGuardRefCount -= 1;
    if (apexNoiseGuardRefCount === 0) {
        window.removeEventListener('unhandledrejection', swallowBenignApexNoise);
    }
};

async function exchangeRelay(relayToken) {
    const response = await fetch(`${apiBase}/auth/relay-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ relayToken }),
    });
    if (!response.ok) throw new Error('Failed to exchange relay token');
}

export async function mount(
    container,
    { basePath = '/', path = '/', onNavigate = () => {}, relayToken, reauthorize } = {}
) {
    console.debug('[RDARR] Mounting RDARR Application');
    if (!container) {
        console.warn('RDARR mount called without container');
        return;
    }

    // Authorize first
    try {
        await exchangeRelay(relayToken);
    } catch (e) {
        // handle relay expiry race with a single refresh
        try {
            const fresh = await reauthorize?.();
            if (!fresh) throw e;
            await exchangeRelay(fresh);
        } catch (err) {
            render(html`<div class="error-page">Authorization failed</div>`, container);
            console.error('[RDARR] Authorization failed:', err);
            return;
        }
    }

    // Register mid-session 401 recovery with Venus's global auth registry.
    // Any fetchOnce call (useGet, useCachedFetch, usePatch, etc.) that gets a
    // 401 will invoke this handler once, coalescing concurrent 401s into a
    // single relay renewal before retrying the original request.
    let unregisterAuth = null;
    if (reauthorize) {
        unregisterAuth = registerUnauthorizedHandler(apiBase, async () => {
            try {
                const fresh = await reauthorize();
                if (!fresh) return false;
                const res = await fetch(`${apiBase}/auth/relay-exchange`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ relayToken: fresh }),
                });
                return res.ok;
            } catch {
                return false;
            }
        });
    }

    installApexNoiseGuard();

    // Re-rendering the same tree with a new path updates the app in place (Preact diffs it). RdarrRoutes
    // keys each page by its route, so a new route still gets a fresh page component - the same result
    // the host used to get by mounting again, without the relay exchange a mount does.
    const renderApp = (currentPath) =>
        render(
            html`
            <${AuthProvider}>
                <${NavigationContext.Provider} value=${{ onNavigate }}>
                    <${App} basePath=${basePath} path=${currentPath} />
                </${NavigationContext.Provider}>
            </${AuthProvider}>
            `,
            container
        );

    renderApp(path);

    let unmounted = false;
    return {
        unmount() {
            if (unmounted) return;
            unmounted = true;
            unregisterAuth?.();
            render(null, container);
            removeApexNoiseGuard();
        },
        // The host calls update() when the URL under RDARR changes (RDARR's own onNavigate, the
        // browser's back/forward, or a link). RDARR renders from the path it is given, so without this
        // the page would not follow the URL.
        update(nextPath) {
            if (unmounted) return;
            renderApp(nextPath ?? '/');
        },
    };
}

// Dev-only auto-mount:
if (process.env.NODE_ENV === 'development') {
    const devRoot = document.getElementById('rdarr-root');
    if (devRoot) {
        // In dev, you can temporarily disable relay by exposing a test endpoint or by running the API locally
        render(html`<div>Dev host not providing relay; please mount from host.</div>`, devRoot);
    }
}
